import Interaction from "../../../models/interactions/Interaction";
import { IFormQuiz, QuizFeedbackSlot } from "../../../models/Quiz";
import {
  QUIZ_TYPES_WITH_FEEDBACK,
  getFeedbackArray,
} from "../../../models/QuizFeedbackHelper";
import Take from "../../../models/Take";

export interface QuizFeedbackLocation {
  takeIndex: number;
  takeId: string;
  quizIndex: number;
  quizId: string;
  slot: QuizFeedbackSlot;
  feedbackIndex: number;
  interaction: Interaction;
}

const slots: QuizFeedbackSlot[] = ["correct", "wrong"];

const slotKey = (slot: QuizFeedbackSlot) =>
  slot === "correct"
    ? "correctFeedbackInteractions"
    : "wrongFeedbackInteractions";

export function findQuizFeedbackInteraction(
  takes: Take[],
  interactionId: string
): QuizFeedbackLocation | undefined {
  for (let takeIndex = 0; takeIndex < takes.length; takeIndex++) {
    const take = takes[takeIndex];
    for (let quizIndex = 0; quizIndex < take.interactions.length; quizIndex++) {
      const quiz = take.interactions[quizIndex];
      if (!QUIZ_TYPES_WITH_FEEDBACK.has(quiz.type)) continue;

      for (const slot of slots) {
        const feedbackArray = getFeedbackArray(quiz as IFormQuiz, slot);
        const feedbackIndex = feedbackArray.findIndex(
          (interaction) => interaction.id === interactionId
        );
        if (feedbackIndex !== -1) {
          return {
            takeIndex,
            takeId: take.id,
            quizIndex,
            quizId: quiz.id,
            slot,
            feedbackIndex,
            interaction: feedbackArray[feedbackIndex],
          };
        }
      }
    }
  }

  return undefined;
}

export function findQuizById(
  takes: Take[],
  quizId: string
): { takeIndex: number; quizIndex: number; quiz: IFormQuiz } | undefined {
  for (let takeIndex = 0; takeIndex < takes.length; takeIndex++) {
    const quizIndex = takes[takeIndex].interactions.findIndex(
      (interaction) =>
        interaction.id === quizId &&
        QUIZ_TYPES_WITH_FEEDBACK.has(interaction.type)
    );
    if (quizIndex !== -1) {
      return {
        takeIndex,
        quizIndex,
        quiz: takes[takeIndex].interactions[quizIndex] as IFormQuiz,
      };
    }
  }

  return undefined;
}

function withUpdatedFeedbackArray<T extends Take>(
  takes: T[],
  takeIndex: number,
  quizIndex: number,
  slot: QuizFeedbackSlot,
  updateArray: (feedbackArray: Interaction[]) => Interaction[]
): T[] {
  const take = takes[takeIndex];
  const quiz = take.interactions[quizIndex] as IFormQuiz;

  const updatedQuiz: IFormQuiz = {
    ...quiz,
    [slotKey(slot)]: updateArray(getFeedbackArray(quiz, slot)),
  };

  const updatedInteractions = [...take.interactions];
  updatedInteractions[quizIndex] = updatedQuiz;

  const updatedTakes = [...takes];
  updatedTakes[takeIndex] = { ...take, interactions: updatedInteractions };
  return updatedTakes;
}

export function replaceQuizFeedbackInteraction<T extends Take>(
  takes: T[],
  location: QuizFeedbackLocation,
  interaction: Interaction
): T[] {
  return withUpdatedFeedbackArray(
    takes,
    location.takeIndex,
    location.quizIndex,
    location.slot,
    (feedbackArray) => {
      const updated = [...feedbackArray];
      updated[location.feedbackIndex] = { ...interaction };
      return updated;
    }
  );
}

export function removeQuizFeedbackInteraction<T extends Take>(
  takes: T[],
  location: QuizFeedbackLocation
): T[] {
  return withUpdatedFeedbackArray(
    takes,
    location.takeIndex,
    location.quizIndex,
    location.slot,
    (feedbackArray) =>
      feedbackArray.filter((_, index) => index !== location.feedbackIndex)
  );
}

export function insertQuizFeedbackInteraction<T extends Take>(
  takes: T[],
  quizId: string,
  slot: QuizFeedbackSlot,
  interaction: Interaction,
  index?: number
): T[] | undefined {
  const quizLocation = findQuizById(takes, quizId);
  if (!quizLocation) return undefined;

  return withUpdatedFeedbackArray(
    takes,
    quizLocation.takeIndex,
    quizLocation.quizIndex,
    slot,
    (feedbackArray) => {
      const updated = [...feedbackArray];
      updated.splice(index ?? updated.length, 0, interaction);
      return updated;
    }
  );
}

export function insertQuizFeedbackInteractions<T extends Take>(
  takes: T[],
  quizId: string,
  slot: QuizFeedbackSlot,
  interactions: Interaction[],
  index?: number
): T[] | undefined {
  const quizLocation = findQuizById(takes, quizId);
  if (!quizLocation) return undefined;

  return withUpdatedFeedbackArray(
    takes,
    quizLocation.takeIndex,
    quizLocation.quizIndex,
    slot,
    (feedbackArray) => {
      const updated = [...feedbackArray];
      updated.splice(index ?? updated.length, 0, ...interactions);
      return updated;
    }
  );
}

export function reorderQuizFeedbackInteractions<T extends Take>(
  takes: T[],
  quizId: string,
  slot: QuizFeedbackSlot,
  startIndex: number,
  endIndex: number
): T[] | undefined {
  const quizLocation = findQuizById(takes, quizId);
  if (!quizLocation) return undefined;

  const feedbackArray = getFeedbackArray(quizLocation.quiz, slot);
  if (
    startIndex < 0 ||
    startIndex >= feedbackArray.length ||
    endIndex < 0 ||
    startIndex === endIndex
  )
    return undefined;

  return withUpdatedFeedbackArray(
    takes,
    quizLocation.takeIndex,
    quizLocation.quizIndex,
    slot,
    (interactions) => {
      const updated = [...interactions];
      const [removed] = updated.splice(startIndex, 1);
      updated.splice(Math.min(endIndex, updated.length), 0, removed);
      return updated;
    }
  );
}

// after removing a feedback interaction: next sibling -> previous sibling -> parent quiz
export function getNextSelectedAfterFeedbackRemoval(
  updatedTakes: Take[],
  location: QuizFeedbackLocation
): string {
  const quiz = updatedTakes[location.takeIndex].interactions[
    location.quizIndex
  ] as IFormQuiz;
  const feedbackArray = getFeedbackArray(quiz, location.slot);
  return (
    feedbackArray[location.feedbackIndex]?.id ??
    feedbackArray[location.feedbackIndex - 1]?.id ??
    location.quizId
  );
}
